import { isAndroid, isWeb } from "@memorang/helpers/platform";
import type { Choice } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import {
	type ComponentProps,
	type ReactNode,
	createContext,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import { Pressable, useWindowDimensions } from "react-native";
import { Menu, Text, TextInput, TouchableRipple } from "react-native-paper";
import { themeRoundnessMultiplier } from "../../../constants";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import MarkdownRenderer from "../../MarkdownRenderer";
import TooltipWrapper from "../../TooltipWrapper";
import InlineBlankChoiceSheet from "./InlineBlankChoiceSheet";
import {
	INLINE_BLANK_TOKEN_REGEX,
	normalizeEscapedInlineBlankTokensInText,
} from "./blank-tokens";

const INLINE_BLANK_TRAILING_INSET_RIGHT = 6;
const INLINE_BLANK_TRAILING_TOUCH_WIDTH = 28;
const INLINE_BLANK_TRAILING_CONTENT_PADDING_RIGHT = 36;

const mergeContentStyleWithTrailingPadding = <
	T extends Record<string, unknown>,
>(
	base: T,
	reserveTrailing: boolean,
): T & { paddingRight?: number } => ({
	...base,
	...(reserveTrailing
		? { paddingRight: INLINE_BLANK_TRAILING_CONTENT_PADDING_RIGHT }
		: {}),
});

export type InlineBlankField = {
	blankId: string;
	choices?: Choice[];
};

type Props = {
	stemText?: string;
	fields: InlineBlankField[];
	values?: Record<string, string> | null;
	defaultValues?: Record<string, string>;
	onChangeValues?: (values: Record<string, string>) => void;
	disabled?: boolean;
	correctValues?: Record<string, string>;
	isValueCorrect?: (value: string, correctValue: string) => boolean;
};

type InlineBlankFieldShellProps = {
	width?: number;
	children: ReactNode;
	trailingOverlay: ReactNode | null;
};

const InlineBlankFieldShell = ({
	width,
	children,
	trailingOverlay,
}: InlineBlankFieldShellProps) => (
	<Box
		style={{
			position: "relative",
			...(width !== undefined ? { width } : {}),
			marginBottom: -8,
			marginHorizontal: 2,
		}}
	>
		{children}
		{trailingOverlay}
	</Box>
);

const getBlankAnswerLabel = (field: InlineBlankField, value: string) =>
	field.choices?.find((choice) => choice.id === value)?.text ?? value;

type InlineBlankAnswerState = "correct" | "incorrect" | "unanswered";

type InlineBlankAnswerChipProps = {
	field: InlineBlankField;
	value: string;
	correctValue: string;
	state: InlineBlankAnswerState;
	fontSize: number;
};

const InlineBlankAnswerChip = ({
	field,
	value,
	correctValue,
	state,
	fontSize,
}: InlineBlankAnswerChipProps) => {
	const theme = useAppTheme();
	const lineHeight = fontSize * 1.5;
	const minHeight = Math.round(lineHeight + 8);
	const successColor = theme.palette.success.main;
	const errorColor = theme.palette.error.main;
	const neutralColor = theme.colors.onSurfaceVariant;
	const valueLabel = getBlankAnswerLabel(field, value);
	const correctLabel = getBlankAnswerLabel(field, correctValue);

	const stateConfig: Record<
		InlineBlankAnswerState,
		{
			icon: ComponentProps<typeof Icon>["name"];
			iconColor: string;
			containerStyle: {
				backgroundColor: string;
				borderColor: string;
				borderStyle: "solid" | "dashed";
			};
			accessibilityLabel: string;
		}
	> = {
		correct: {
			icon: "check-circle-outline",
			iconColor: successColor,
			containerStyle: {
				backgroundColor: theme.palette.answer.correct.background,
				borderColor: theme.palette.answer.correct.border,
				borderStyle: "solid",
			},
			accessibilityLabel: `Correct answer: ${valueLabel}`,
		},
		incorrect: {
			icon: "close-circle-outline",
			iconColor: errorColor,
			containerStyle: {
				backgroundColor: theme.palette.answer.incorrect.background,
				borderColor: theme.palette.answer.incorrect.border,
				borderStyle: "solid",
			},
			accessibilityLabel: `Incorrect. Your answer: ${valueLabel}. Correct answer: ${correctLabel}`,
		},
		unanswered: {
			icon: "minus-circle-outline",
			iconColor: neutralColor,
			containerStyle: {
				backgroundColor: theme.colors.surfaceVariant,
				borderColor: neutralColor,
				borderStyle: "dashed",
			},
			accessibilityLabel: `Not answered. Correct answer: ${correctLabel}`,
		},
	};

	const { icon, iconColor, containerStyle, accessibilityLabel } =
		stateConfig[state];

	return (
		<Box
			accessibilityLabel={accessibilityLabel}
			style={{
				alignItems: "center",
				alignSelf: "flex-start",
				borderRadius: theme.roundness * themeRoundnessMultiplier,
				borderWidth: 1,
				flexDirection: "row",
				gap: 6,
				minHeight,
				paddingLeft: 6,
				paddingRight: 8,
				...containerStyle,
			}}
		>
			<Box height={lineHeight} justifyContent="center">
				<Icon color={iconColor} name={icon} size={16} />
			</Box>
			{state === "correct" ? (
				<Text
					style={{
						color: successColor,
						fontSize,
						fontWeight: "500",
						lineHeight,
					}}
				>
					{valueLabel}
				</Text>
			) : null}
			{state === "incorrect" ? (
				<>
					<Text
						style={{
							color: errorColor,
							fontSize,
							lineHeight,
							textDecorationLine: "line-through",
						}}
					>
						{valueLabel}
					</Text>
					<Text
						style={{
							color: neutralColor,
							fontSize,
							fontWeight: "500",
							lineHeight,
						}}
					>
						{correctLabel}
					</Text>
				</>
			) : null}
			{state === "unanswered" ? (
				<Text
					style={{
						color: neutralColor,
						fontSize,
						fontWeight: "500",
						lineHeight,
					}}
				>
					{correctLabel}
				</Text>
			) : null}
		</Box>
	);
};

type TrailingInlineControlProps = {
	onPress: () => void;
	accessibilityLabel: string;
	children: ReactNode;
};

const TrailingInlineControl = ({
	onPress,
	accessibilityLabel,
	children,
}: TrailingInlineControlProps) => (
	<Pressable
		accessibilityLabel={accessibilityLabel}
		accessibilityRole="button"
		hitSlop={{ top: 4, bottom: 4, left: 4, right: 0 }}
		onPress={onPress}
		style={{
			position: "absolute",
			alignItems: "center",
			bottom: 0,
			justifyContent: "center",
			right: INLINE_BLANK_TRAILING_INSET_RIGHT,
			top: 0,
			width: INLINE_BLANK_TRAILING_TOUCH_WIDTH,
			zIndex: 1,
		}}
		tabIndex={-1}
	>
		{children}
	</Pressable>
);

const INLINE_BLANK_CHAR_WIDTH = 10;
const INLINE_BLANK_HORIZONTAL_PADDING = 48;

const getLongestChoiceLabelLength = (field: InlineBlankField) =>
	Math.max(
		...(field.choices?.map(
			(choice) => choice.text?.length ?? choice.id.length,
		) ?? [0]),
	);

const SELECT_BLANK_MAX_WIDTH = 240;

const getInlineBlankWidth = (field: InlineBlankField, value: string) => {
	const hasChoices = Boolean(field.choices?.length);
	const referenceLength = hasChoices
		? Math.max(getLongestChoiceLabelLength(field), 6)
		: Math.max(value.length, 6);
	const calculatedWidth =
		referenceLength * INLINE_BLANK_CHAR_WIDTH + (hasChoices ? 86 : 42);
	const minWidth = 140;
	const maxWidth = hasChoices ? SELECT_BLANK_MAX_WIDTH : 320;

	return Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
};

const DROPDOWN_MENU_MIN_WIDTH = 200;
const DROPDOWN_MENU_MAX_WIDTH = 480;
const DROPDOWN_MENU_WINDOW_MARGIN = 32;

const getDropdownMenuWidth = (
	field: InlineBlankField,
	anchorWidth: number,
	windowWidth: number,
) => {
	const longestLabelLength = getLongestChoiceLabelLength(field);
	const contentWidth =
		longestLabelLength * INLINE_BLANK_CHAR_WIDTH +
		INLINE_BLANK_HORIZONTAL_PADDING;

	return Math.min(
		Math.max(DROPDOWN_MENU_MIN_WIDTH, anchorWidth, contentWidth),
		DROPDOWN_MENU_MAX_WIDTH,
		windowWidth - DROPDOWN_MENU_WINDOW_MARGIN,
	);
};

type BlankValuesStore = {
	getSnapshot: (blankId: string) => string;
	subscribe: (blankId: string, listener: () => void) => () => void;
	updateAll: (next: Record<string, string>) => void;
};

const createBlankValuesStore = (
	initial: Record<string, string>,
): BlankValuesStore => {
	let snapshot = initial;
	const listenersByBlankId = new Map<string, Set<() => void>>();

	const notifyBlank = (blankId: string) => {
		for (const listener of listenersByBlankId.get(blankId) ?? []) {
			listener();
		}
	};

	return {
		getSnapshot: (blankId: string) => snapshot[blankId] ?? "",
		subscribe: (blankId: string, listener: () => void) => {
			let listeners = listenersByBlankId.get(blankId);
			if (!listeners) {
				listeners = new Set();
				listenersByBlankId.set(blankId, listeners);
			}
			listeners.add(listener);
			return () => {
				listeners?.delete(listener);
			};
		},
		updateAll: (next: Record<string, string>) => {
			const prev = snapshot;
			snapshot = next;
			const blankIds = new Set([...Object.keys(prev), ...Object.keys(next)]);
			for (const blankId of blankIds) {
				if (prev[blankId] !== next[blankId]) {
					notifyBlank(blankId);
				}
			}
		},
	};
};

const BlankValuesContext = createContext<BlankValuesStore | null>(null);

const useBlankFieldValue = (blankId: string): string => {
	const valuesStore = use(BlankValuesContext);
	if (!valuesStore) {
		throw new Error(
			"useBlankFieldValue must be used within BlankValuesContext",
		);
	}
	return useSyncExternalStore(
		(onStoreChange) => valuesStore.subscribe(blankId, onStoreChange),
		() => valuesStore.getSnapshot(blankId),
	);
};

const InlineBlankFieldInput = ({
	field,
	onChangeValue,
	disabled,
	fontSize,
	correctValue,
	isValueCorrect,
}: {
	field: InlineBlankField;
	onChangeValue: (value: string) => void;
	disabled: boolean;
	fontSize: number;
	correctValue?: string;
	isValueCorrect?: (value: string, correctValue: string) => boolean;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}) => {
	const value = useBlankFieldValue(field.blankId);
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { width: windowWidth } = useWindowDimensions();
	const isNative = !isWeb;
	const lineHeight = fontSize * 1.5;
	const hasChoices = Boolean(field.choices?.length);
	const inputHeight = Math.round(lineHeight + 8);
	const [selectMenuVisible, setSelectMenuVisible] = useState(false);
	const [draftValue, setDraftValue] = useState(value);
	const isFocusedRef = useRef(false);

	useEffect(() => {
		if (!isFocusedRef.current) {
			setDraftValue(value);
		}
	}, [value]);

	const handleClear = useCallback(() => {
		setDraftValue("");
		onChangeValue("");
	}, [onChangeValue]);

	const showClear = Boolean(draftValue && !disabled);

	if (disabled && correctValue !== undefined) {
		const checkIsCorrect =
			isValueCorrect ?? ((value: string, correct: string) => value === correct);
		const hasResponse = draftValue.trim().length > 0;
		const answerState: InlineBlankAnswerState = hasResponse
			? checkIsCorrect(draftValue, correctValue)
				? "correct"
				: "incorrect"
			: "unanswered";

		return (
			<InlineBlankFieldShell trailingOverlay={null}>
				<InlineBlankAnswerChip
					field={field}
					value={draftValue}
					correctValue={correctValue}
					state={answerState}
					fontSize={fontSize}
				/>
			</InlineBlankFieldShell>
		);
	}

	const sharedInputProps = {
		mode: "outlined" as const,
		dense: true,
		disabled,
		outlineColor: theme.colors.surfaceVariant,
		activeOutlineColor: theme.colors.primary,
		contentStyle: {
			fontSize,
			lineHeight: isWeb ? inputHeight - 8 : isAndroid ? 18 : 0,
			paddingHorizontal: 8,
			paddingVertical: 0,
			marginVertical: 0,
			...(isAndroid ? { textAlignVertical: "center" as const } : {}),
		},
		theme: {
			colors: {
				background: "transparent",
			},
		},
		style: {
			height: inputHeight,
			backgroundColor: "transparent",
			borderRadius: isNative ? 8 : 8,
		},
	};

	if (hasChoices) {
		const selectedOption = field.choices?.find(
			(choice) => choice.id === draftValue,
		);
		const blankWidth = getInlineBlankWidth(field, selectedOption?.text || "");
		const menuWidth = getDropdownMenuWidth(field, blankWidth, windowWidth);

		const anchor = (
			<Pressable
				disabled={disabled}
				onPress={() => {
					if (!disabled) {
						setSelectMenuVisible(true);
					}
				}}
				style={{ width: "100%" }}
			>
				<TextInput
					{...sharedInputProps}
					outlineColor={
						selectMenuVisible
							? theme.colors.primary
							: theme.colors.outlineVariant
					}
					contentStyle={mergeContentStyleWithTrailingPadding(
						sharedInputProps.contentStyle,
						true,
					)}
					editable={false}
					placeholder="Select"
					placeholderTextColor={theme.colors.onSurfaceVariant}
					pointerEvents="none"
					showSoftInputOnFocus={false}
					value={selectedOption?.text || ""}
				/>
			</Pressable>
		);

		return (
			<InlineBlankFieldShell
				trailingOverlay={
					showClear ? (
						<TrailingInlineControl
							accessibilityLabel="Clear answer"
							onPress={handleClear}
						>
							<Icon color={theme.colors.primary} name="close" size={14} />
						</TrailingInlineControl>
					) : (
						<TrailingInlineControl
							accessibilityLabel={
								selectMenuVisible ? "Close choice menu" : "Open choice menu"
							}
							onPress={() => {
								if (!disabled) {
									setSelectMenuVisible((prev) => !prev);
								}
							}}
						>
							<Icon
								color={theme.colors.primary}
								name={selectMenuVisible ? "chevron-up" : "chevron-down"}
								size={16}
							/>
						</TrailingInlineControl>
					)
				}
				width={blankWidth}
			>
				{isMobile ? (
					<>
						{anchor}
						<InlineBlankChoiceSheet
							visible={selectMenuVisible}
							onDismiss={() => setSelectMenuVisible(false)}
							choices={field.choices ?? []}
							selectedId={draftValue}
							onSelectChoice={(choiceId) => {
								setDraftValue(choiceId);
								onChangeValue(choiceId);
							}}
						/>
					</>
				) : (
					<Menu
						visible={selectMenuVisible}
						onDismiss={() => setSelectMenuVisible(false)}
						anchorPosition="bottom"
						contentStyle={{
							backgroundColor: theme.colors.surface,
							paddingVertical: 4,
							minWidth: menuWidth,
							width: menuWidth,
						}}
						anchor={
							selectedOption?.text ? (
								<TooltipWrapper
									title={selectedOption.text}
									display="block"
									style={{ width: "100%" }}
								>
									{anchor}
								</TooltipWrapper>
							) : (
								anchor
							)
						}
					>
						{field.choices?.map((choice) => (
							<TouchableRipple
								key={choice.id}
								onPress={() => {
									setDraftValue(choice.id);
									onChangeValue(choice.id);
									setSelectMenuVisible(false);
								}}
								style={{
									paddingHorizontal: 12,
									paddingVertical: 8,
									width: menuWidth,
								}}
							>
								<Text variant="bodyMedium">{choice.text ?? choice.id}</Text>
							</TouchableRipple>
						))}
					</Menu>
				)}
			</InlineBlankFieldShell>
		);
	}

	return (
		<InlineBlankFieldShell
			trailingOverlay={
				showClear ? (
					<TrailingInlineControl
						accessibilityLabel="Clear answer"
						onPress={handleClear}
					>
						<Icon color={theme.colors.primary} name="close" size={12} />
					</TrailingInlineControl>
				) : null
			}
			width={getInlineBlankWidth(field, draftValue)}
		>
			<TextInput
				{...sharedInputProps}
				contentStyle={mergeContentStyleWithTrailingPadding(
					sharedInputProps.contentStyle,
					showClear,
				)}
				editable={!disabled}
				pointerEvents={disabled ? "none" : "auto"}
				onBlur={() => {
					isFocusedRef.current = false;
				}}
				onChangeText={(nextValue) => {
					setDraftValue(nextValue);
					onChangeValue(nextValue);
				}}
				onFocus={() => {
					isFocusedRef.current = true;
				}}
				value={draftValue}
			/>
		</InlineBlankFieldShell>
	);
};

const InlineBlankFields = ({
	stemText,
	fields,
	values,
	defaultValues,
	onChangeValues,
	disabled = false,
	correctValues,
	isValueCorrect,
}: Props) => {
	const valuesStoreRef = useRef<BlankValuesStore | null>(null);
	if (!valuesStoreRef.current) {
		valuesStoreRef.current = createBlankValuesStore(
			values ?? defaultValues ?? {},
		);
	}
	const valuesStore = valuesStoreRef.current;

	useEffect(() => {
		if (values != null) {
			valuesStore.updateAll(values);
		}
	}, [values, valuesStore]);

	const onChangeValuesRef = useRef(onChangeValues);
	onChangeValuesRef.current = onChangeValues;

	const stemForBlanks = useMemo(
		() =>
			stemText ? normalizeEscapedInlineBlankTokensInText(stemText) : stemText,
		[stemText],
	);

	const handleChangeValue = useCallback(
		(blankId: string, nextValue: string) => {
			const nextValues = {
				...Object.fromEntries(
					fields.map((field) => [
						field.blankId,
						valuesStore.getSnapshot(field.blankId),
					]),
				),
				[blankId]: nextValue,
			};
			valuesStore.updateAll(nextValues);
			onChangeValuesRef.current?.(nextValues);
		},
		[fields, valuesStore],
	);

	const fieldCallbacks = useMemo(
		() =>
			fields.reduce(
				(acc, field) => {
					acc[field.blankId] = (nextValue: string) =>
						handleChangeValue(field.blankId, nextValue);
					return acc;
				},
				{} as Record<string, (v: string) => void>,
			),
		[fields, handleChangeValue],
	);

	const renderBlank = useCallback(
		(index: number) => {
			const field = fields[index];

			if (!field?.blankId) {
				return null;
			}

			return (
				<InlineBlankFieldInput
					key={field.blankId}
					field={field}
					onChangeValue={fieldCallbacks[field.blankId]}
					disabled={disabled}
					fontSize={16}
					correctValue={correctValues?.[field.blankId]}
					isValueCorrect={isValueCorrect}
				/>
			);
		},
		[disabled, fields, fieldCallbacks, correctValues, isValueCorrect],
	);

	const markdownWithBlanks = useMemo(() => {
		if (!stemForBlanks) {
			return null;
		}
		if (!INLINE_BLANK_TOKEN_REGEX.test(stemForBlanks)) {
			return null;
		}
		return (
			<MarkdownRenderer
				text={stemForBlanks}
				fontSize={16}
				variant="stem"
				renderBlank={renderBlank}
			/>
		);
	}, [stemForBlanks, renderBlank]);

	if (markdownWithBlanks) {
		return (
			<BlankValuesContext value={valuesStore}>
				{markdownWithBlanks}
			</BlankValuesContext>
		);
	}

	return null;
};

export default InlineBlankFields;
